import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Users,
  Briefcase,
  ClipboardList,
  BarChart3,
  Shield,
  Building,
  UserCheck,
  Trophy,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  ChevronLeft,
  ChevronRight,
  Database,
  Bell,
} from "lucide-react";
import {
  getFirestore,
  collection,
  getDocs,
  getDoc,
  setDoc,
  query,
  orderBy,
  limit,
  startAfter,
  deleteDoc,
  doc,
} from "firebase/firestore";

import { firebaseApp, getSecondaryApp } from "../../firebase.js";
import {
  getAuth,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { toast } from "react-hot-toast";
import UserSessionsTab from "../../pages/AdminPanel/UserSessionTab.jsx";
import HRRecruitersTab from "../../pages/AdminPanel/HRRecruitersTab.jsx";
import JobseekersTab from "../../pages/AdminPanel/JobseekersTab.jsx";
import { safeToDate } from "../../utils/SafeToDate.jsx";
import DemoRequestTab from "../../pages/AdminPanel/DemoRequestTab.jsx";
import AddCandidateTab from "../../pages/AdminPanel/AddCandidateTab.jsx";
import MigrateTests from "../../pages/Admin/MigrateTests.jsx";
import ProPlusNotifyTab from "../../pages/AdminPanel/ProPlusNotifyTab.jsx";
import Pagination from "../Pagination.jsx";
import { usePagination } from "../../hooks/usePagination.js";

const AdminPanelDashboard = () => {
  const [hrUsers, setHRUsers] = useState([]);
  const [jobSeekers, setJobSeekers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionStats, setSessionStats] = useState({
    totalSessions: 0,
    activeSessions: 0,
    avgSessionDuration: 0,
    totalUsersToday: 0,
  });
  const [requests, setRequests] = useState([]);
  const [sessionFilter, setSessionFilter] = useState("all");
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [tests, setTests] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('hr');
  const [expandedSections, setExpandedSections] = useState({
    hrUsers: true,
    hrTests: true,
    candidates: true,
    candidateResults: true,
  });
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const tabScrollRef = useRef(null);

  const db = getFirestore(firebaseApp);
  const auth = getAuth();

  // Check scroll position to show/hide arrows
  const checkScrollPosition = () => {
    if (tabScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabScrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Scroll left
  const scrollLeft = () => {
    if (tabScrollRef.current) {
      tabScrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  // Scroll right
  const scrollRight = () => {
    if (tabScrollRef.current) {
      tabScrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Check scroll position on mount and window resize
  useEffect(() => {
    checkScrollPosition();
    window.addEventListener('resize', checkScrollPosition);
    return () => window.removeEventListener('resize', checkScrollPosition);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchHRUsers(),
        fetchJobSeekers(),
        fetchUserSessions(),
        fetchTests(),
        fetchTestResults(),
        fetchDemoRequests(),
      ]);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHRUsers = async () => {
    try {
      const recruitersRef = collection(db, "recruiters");
      const querySnapshot = await getDocs(recruitersRef);

      const users = [];
      querySnapshot.forEach((doc) => {
        console.log("👤 Fetched HR User:", {
          id: doc.id,
          name: doc.data().name,
          email: doc.data().email
        });
        users.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      console.log("✅ Total HR users fetched:", users.length);
      console.log("All HR user IDs:", users.map(u => u.id));
      setHRUsers(users);
    } catch (error) {
      console.error("❌ Error fetching HR users:", error);
    }
  };

  const fetchJobSeekers = async () => {
    try {
      const jobSeekersRef = collection(db, "jobseekers");
      const q = query(jobSeekersRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);

      const seekers = [];
      querySnapshot.forEach((doc) => {
        seekers.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setJobSeekers(seekers);
    } catch (error) {
      console.error("Error fetching job seekers:", error);
    }
  };

  const fetchUserSessions = async () => {
    try {
      const sessionsRef = collection(db, "userSessions");
      const q = query(sessionsRef, orderBy("loginTime", "desc"));
      const querySnapshot = await getDocs(q);
      const sessions = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        sessions.push({
          id: doc.id,
          ...data,
          userType: data.userType || "jobseeker",
          loginTime:
            typeof data.loginTime === "number"
              ? new Date(data.loginTime)
              : safeToDate(data.loginTime),
          logoutTime:
            typeof data.logoutTime === "number"
              ? new Date(data.logoutTime)
              : data.logoutTime
              ? safeToDate(data.logoutTime)
              : null,
          createdAt: safeToDate(data.createdAt),
        });
      });
      console.log("Fetched sessions:", sessions);
      setSessions(sessions);
      calculateSessionStats(sessions);
    } catch (error) {
      console.error("Error fetching user sessions:", error);
    }
  };

  const fetchTests = async () => {
    console.log("🚀 Starting to fetch HR tests from 'hrTests' collection...");
    try {
      const testsRef = collection(db, "hrTests");
      const querySnapshot = await getDocs(testsRef);

      console.log("📦 Query snapshot size:", querySnapshot.size);
      console.log("📦 Query empty?:", querySnapshot.empty);

      const allTests = [];
      querySnapshot.forEach((doc) => {
        const testData = doc.data();
        console.log("📝 Fetched HR Test:", {
          id: doc.id,
          hrId: testData.hrId,
          hrEmail: testData.hrEmail,
          jobTitle: testData.jobTitle,
          title: testData.title
        });
        allTests.push({
          id: doc.id,
          ...testData,
        });
      });

      console.log("✅ Total HR tests fetched:", allTests.length);
      console.log("📋 All test hrIds:", allTests.map(t => t.hrId));
      setTests(allTests);
    } catch (error) {
      console.error("❌ Error fetching tests:", error);
      console.error("❌ Error details:", error);
    }
  };

  const fetchTestResults = async () => {
    try {
      const resultsRef = collection(db, "jobseekerResults");
      const q = query(resultsRef, orderBy("completedAt", "desc"));
      const querySnapshot = await getDocs(q);

      const results = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        console.log("📊 Test Result Data:", {
          id: doc.id,
          testId: data.testId,
          jobSeekerId: data.jobSeekerId,
          jobTitle: data.jobTitle,
          testTitle: data.testTitle,
          score: data.totalScore || data.score,
          allFields: Object.keys(data)
        });

        results.push({
          id: doc.id,
          testId: data.testId,
          userId: data.jobSeekerId,
          score: data.totalScore || data.score,
          completedAt: data.completedAt,
          userName: data.userName,
          testTitle: data.jobTitle,
        });
      });

      console.log("✅ Total test results fetched:", results.length);
      setTestResults(results);
    } catch (error) {
      console.error("Error fetching test results:", error);
    }
  };

  async function fetchDemoRequests() {
    const snapshot = await getDocs(collection(db, "demoRequests"));
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        submittedAt: safeToDate(data.submittedAt) || new Date()
      };
    });
  }

  const calculateSessionStats = (sessionData) => {
    const totalSessions = sessionData.length;
    const activeSessions = sessionData.filter(
      (session) => session.logoutTime == null
    ).length;
    const completedSessions = sessionData.filter(
      (session) => typeof session.sessionDuration === "number"
    );

    const avgDuration =
      completedSessions.length > 0
        ? completedSessions.reduce(
            (sum, session) => sum + (session.sessionDuration || 0),
            0
          ) / completedSessions.length
        : 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todaySessions = sessionData.filter(
      (session) =>
        session.loginTime instanceof Date &&
        !isNaN(session.loginTime.getTime()) &&
        session.loginTime >= today
    );

    const uniqueUsersToday = new Set(
      todaySessions.map((session) => session.userId)
    ).size;

    setSessionStats({
      totalSessions,
      activeSessions,
      avgSessionDuration: Math.round(avgDuration / 1000 / 60),
      totalUsersToday: uniqueUsersToday,
    });
  };

  /**
   * Function to add candidate data to Firestore.
   * Inserts into 'jobseekers' collection if userType is jobseeker,
   * or 'HRS' collection if userType is HR.
   * @param candidateData - candidate info from AddCandidateTab form
   */
  const handleAddCandidate = async (
    candidateData
  ) => {
    const { email, password, name, company, userType } = candidateData;

    try {
      // Verify admin is logged in
      const adminUser = auth.currentUser;
      if (!adminUser) {
        throw new Error("You must be logged in to perform this action");
      }

      const adminEmail = adminUser.email;
      if (!adminEmail) {
        throw new Error("Admin email not found");
      }

      // Check if user is admin
      let isAdmin = false;
      if (adminEmail === "admin@vetriaai.com") {
        isAdmin = true;
      } else {
        try {
          const adminDoc = await getDoc(doc(db, "admins", adminUser.uid));
          isAdmin = adminDoc.exists();
        } catch (error) {
          console.warn("Could not check admin status in Firestore:", error);
          isAdmin = false;
        }
      }

      if (!isAdmin) {
        throw new Error("Only administrators can add new users");
      }

      // Use secondary Firebase app to create user WITHOUT affecting admin's auth state
      const secondaryApp = getSecondaryApp();
      const secondaryAuth = getAuth(secondaryApp);

      // Create new user using secondary auth instance
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        email,
        password
      );
      const newUserUid = userCredential.user.uid;

      // Create user profile in Firestore (admin has permission)
      const collectionName =
        userType === "jobseeker" ? "jobseekers" : "recruiters";

      await setDoc(doc(db, collectionName, newUserUid), {
        name: candidateData.name,
        email: candidateData.email,
        userType: candidateData.userType,
        company: candidateData.company || null,
        createdAt: new Date(),
      });

      // Sign out from secondary auth to clean up
      await secondaryAuth.signOut();

      // Show success message - admin remains logged in!
      toast.success(
        `${
          userType === "jobseeker" ? "Job seeker" : "Recruiter"
        } added successfully!`
      );

      // Refresh the user list
      if (userType === "jobseeker") {
        await fetchJobSeekers();
      } else {
        await fetchHRUsers();
      }

      return newUserUid;
    } catch (error) {
      console.error("Error adding user:", error);

      if (error.code === "permission-denied") {
        toast.error("Access denied. Administrator privileges required.");
      } else if (error.code === "auth/email-already-in-use") {
        toast.error("This email address is already registered.");
      } else if (error.code === "auth/invalid-email") {
        toast.error("Invalid email address.");
      } else if (error.code === "auth/weak-password") {
        toast.error("Password must be at least 6 characters.");
      } else if (error.message.includes("admin")) {
        toast.error("Only administrators can perform this action.");
      } else {
        toast.error("Failed to add user. Please try again.");
      }

      throw error;
    }
  };
  ////handling delete candidate///////////////////
  const handleDeleteCandidate = async (id, userType) => {
    if (!id) return;
    const collectionName = userType === "jobseeker" ? "jobseekers" : "recruiters";
    try {
     
      await deleteDoc(doc(db, collectionName, id));
      
      // Update UI by removing deleted candidate from state
      if (userType === "jobseeker") {
        setJobSeekers((prev) => prev.filter((seeker) => seeker.id !== id));
      } else {
        setHRUsers((prev) => prev.filter((hr) => hr.id !== id));
      }

      toast.success("Candidate deleted successfully");
    } catch (error) {
      console.error("Failed to delete candidate:", error);
      toast.error("Error deleting candidate, try again.");
    }
  };

  useEffect(() => {
    if (activeTab === 'demoRequests') {
      fetchDemoRequests().then(setRequests);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "sessions") {
      fetchUserSessions();
    }
  }, [activeTab]);

  // Group sessions by user before pagination
  const groupedSessions = useMemo(() => {
    // Apply time-based filter first
    let filteredSessions = sessions;

    if (sessionFilter === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      filteredSessions = sessions.filter(session =>
        session.loginTime instanceof Date &&
        session.loginTime >= today
      );
    } else if (sessionFilter === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      weekAgo.setHours(0, 0, 0, 0);
      filteredSessions = sessions.filter(session =>
        session.loginTime instanceof Date &&
        session.loginTime >= weekAgo
      );
    } else if (sessionFilter === 'month') {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      monthAgo.setHours(0, 0, 0, 0);
      filteredSessions = sessions.filter(session =>
        session.loginTime instanceof Date &&
        session.loginTime >= monthAgo
      );
    } else if (sessionFilter === 'active') {
      filteredSessions = sessions.filter(session => !session.logoutTime);
    }

    const grouped = new Map();

    filteredSessions.forEach(session => {
      const key = session.userId;
      const existing = grouped.get(key);

      if (existing) {
        existing.totalSessions += 1;
        if (!session.logoutTime) {
          existing.activeSessions += 1;
        }

        // Update to latest login time
        const sessionLoginTime = session.loginTime instanceof Date ? session.loginTime : null;
        if (sessionLoginTime && (!existing.lastLoginTime || sessionLoginTime > existing.lastLoginTime)) {
          existing.lastLoginTime = sessionLoginTime;
          existing.lastLogoutTime = session.logoutTime instanceof Date ? session.logoutTime : null;
          existing.lastSessionDuration = session.sessionDuration;
          existing.isActive = !session.logoutTime;
        }
      } else {
        grouped.set(key, {
          userId: session.userId,
          userEmail: session.userEmail,
          userType: session.userType,
          totalSessions: 1,
          activeSessions: session.logoutTime ? 0 : 1,
          lastLoginTime: session.loginTime instanceof Date ? session.loginTime : null,
          lastLogoutTime: session.logoutTime instanceof Date ? session.logoutTime : null,
          lastSessionDuration: session.sessionDuration,
          isActive: !session.logoutTime,
        });
      }
    });

    return Array.from(grouped.values());
  }, [sessions, sessionFilter]);

  // Apply user type filter before pagination
  const filteredGroupedSessions = useMemo(() => {
    if (userTypeFilter === 'all') {
      return groupedSessions;
    }
    return groupedSessions.filter(session => session.userType === userTypeFilter);
  }, [groupedSessions, userTypeFilter]);

  // Use pagination hook on filtered grouped data
  const { paginatedData, currentPage, totalPages, handlePageChange } =
    usePagination(filteredGroupedSessions, 8);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return (
        date.toLocaleDateString() +
        " " +
        date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
    } catch {
      return "N/A";
    }
  };

  const getTestsForHR = (hrId) => {
    const hrTests = tests.filter((test) => test.hrId === hrId);
    console.log(`🔍 Filtering tests for HR ID: ${hrId}`, {
      totalTests: tests.length,
      matchingTests: hrTests.length,
      hrTestIds: hrTests.map(t => t.id)
    });
    return hrTests;
  };

  const getResultsForTest = (testId) => {
    return testResults.filter((result) => result.testId === testId);
  };

  const getResultsForJobSeeker = (seekerId) => {
    return testResults.filter((result) => result.userId === seekerId);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-6 md:mb-8">
          <div className="bg-gradient-to-r from-red-800 to-red-600 text-white rounded-lg sm:rounded-xl p-4 sm:p-5 md:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold flex items-center flex-wrap">
                  <Shield className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 mr-3 flex-shrink-0" />
                  <span>VetriaAI Admin Panel</span>
                </h1>
                <p className="text-red-100 mt-1 sm:mt-2 text-xs sm:text-sm md:text-base">
                  Complete platform overview and user management
                </p>
              </div>
              <div className="text-left sm:text-right w-full sm:w-auto">
                <div className="text-xl sm:text-2xl font-bold">
                  {hrUsers.length + jobSeekers.length}
                </div>
                <div className="text-red-100 text-xs sm:text-sm">Total Users</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6 md:mb-8">
          <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 truncate">HR/Recruiters</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mt-0.5 sm:mt-1">
                  {hrUsers.length}
                </p>
              </div>
              <Briefcase className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-blue-600 flex-shrink-0 ml-2" />
            </div>
          </div>

          <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 truncate">Job Seekers</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mt-0.5 sm:mt-1">
                  {jobSeekers.length}
                </p>
              </div>
              <Users className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-green-600 flex-shrink-0 ml-2" />
            </div>
          </div>

          <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 truncate">Total Tests</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mt-0.5 sm:mt-1">
                  {tests.length}
                </p>
              </div>
              <ClipboardList className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-purple-600 flex-shrink-0 ml-2" />
            </div>
          </div>

          <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 truncate">Test Results</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mt-0.5 sm:mt-1">
                  {testResults.length}
                </p>
              </div>
              <BarChart3 className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-red-600 flex-shrink-0 ml-2" />
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-4 sm:mb-6 relative">
          {/* Left Arrow */}
          {showLeftArrow && (
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-0 bottom-0 z-10 bg-gradient-to-r from-gray-50 via-gray-50 to-transparent w-12 flex items-center justify-start pl-1 lg:hidden"
              aria-label="Scroll left"
            >
              <div className="bg-white rounded-full p-1 shadow-md border border-gray-200 hover:bg-gray-100 transition">
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </div>
            </button>
          )}

          {/* Right Arrow */}
          {showRightArrow && (
            <button
              onClick={scrollRight}
              className="absolute right-0 top-0 bottom-0 z-10 bg-gradient-to-l from-gray-50 via-gray-50 to-transparent w-12 flex items-center justify-end pr-1 lg:hidden"
              aria-label="Scroll right"
            >
              <div className="bg-white rounded-full p-1 shadow-md border border-gray-200 hover:bg-gray-100 transition">
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </div>
            </button>
          )}

          <div
            ref={tabScrollRef}
            onScroll={checkScrollPosition}
            className="border-b border-gray-200 overflow-x-auto overflow-y-hidden scrollbar-hide"
          >
            <nav className="-mb-px flex space-x-2 sm:space-x-4 md:space-x-6 lg:space-x-8 w-max lg:w-auto">
              <button
                onClick={() => setActiveTab("hr")}
                className={`py-2 sm:py-3 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${
                  activeTab === "hr"
                    ? "border-red-500 text-red-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Briefcase className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2 flex-shrink-0" />
                <span className="hidden sm:inline">HR / Recruiters</span>
                <span className="sm:hidden">HR</span>
              </button>
              <button
                onClick={() => setActiveTab("candidates")}
                className={`py-2 sm:py-3 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${
                  activeTab === "candidates"
                    ? "border-red-500 text-red-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2 flex-shrink-0" />
                <span className="hidden sm:inline">Candidates / Job Seekers</span>
                <span className="sm:hidden">Candidates</span>
              </button>

              <button
                onClick={() => setActiveTab("sessions")}
                className={`py-2 sm:py-3 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${
                  activeTab === "sessions"
                    ? "border-red-500 text-red-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2 flex-shrink-0" />
                <span>Sessions</span>
              </button>

              <button
                onClick={() => setActiveTab("demoRequests")}
                className={`py-2 sm:py-3 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${
                  activeTab === "demoRequests"
                    ? "border-red-500 text-red-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2 flex-shrink-0" />
                <span className="hidden sm:inline">Demo Requests</span>
                <span className="sm:hidden">Demos</span>
              </button>
              <button
                className={`py-2 sm:py-3 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${
                  activeTab === "addCandidates"
                    ? "border-red-500 text-red-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                onClick={() => setActiveTab("addCandidates")}
              >
                <UserCheck className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2 flex-shrink-0" />
                <span className="hidden sm:inline">Add Candidates</span>
                <span className="sm:hidden">Add</span>
              </button>
              <button
                className={`py-2 sm:py-3 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${
                  activeTab === "migration"
                    ? "border-red-500 text-red-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                onClick={() => setActiveTab("migration")}
              >
                <Database className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2 flex-shrink-0" />
                <span className="hidden sm:inline">Migrate Tests</span>
                <span className="sm:hidden">Migrate</span>
              </button>
              <button
                className={`py-2 sm:py-3 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${
                  activeTab === "proPlusNotify"
                    ? "border-red-500 text-red-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                onClick={() => setActiveTab("proPlusNotify")}
              >
                <Bell className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2 flex-shrink-0" />
                <span className="hidden sm:inline">Pro Plus Notify</span>
                <span className="sm:hidden">Pro+</span>
              </button>
            </nav>
          </div>
        </div>

        {/* HR Section */}
        {activeTab === "hr" && (
          <HRRecruitersTab
            hrUsers={hrUsers}
            tests={tests}
            testResults={testResults}
            expandedSections={{
              hrUsers: expandedSections.hrUsers,
              hrTests: expandedSections.hrTests,
            }}
            toggleSection={toggleSection}
            formatDate={formatDate}
            getTestsForHR={getTestsForHR}
            getResultsForTest={getResultsForTest}
            onRefresh={fetchAllData}
          />
        )}

        {/* Candidates Section */}
        {activeTab === "candidates" && (
          <JobseekersTab
            jobSeekers={jobSeekers}
            tests={tests}
            testResults={testResults}
            expandedSections={{
              candidates: expandedSections.candidates,
              candidateResults: expandedSections.candidateResults,
            }}
            toggleSection={toggleSection}
            formatDate={formatDate}
            getResultsForJobSeeker={getResultsForJobSeeker}
            onRefresh={fetchAllData}
          />
        )}

        {activeTab === "sessions" && (
          <>
            <UserSessionsTab
              //sessions={paginatedData} // send sliced data
              sessions={paginatedData}
              allRawSessions={sessions}
              sessionStats={sessionStats}
              sessionFilter={sessionFilter}
              setSessionFilter={setSessionFilter}
              userTypeFilter={userTypeFilter}
              setUserTypeFilter={setUserTypeFilter}
              fetchUserSessions={fetchUserSessions}
            />
            {/* ✅ Pagination placed right below the tab */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              disabled={sessions.length === 0}
            />
          </>
        )}

        {activeTab === "demoRequests" && <DemoRequestTab requests={requests} />}

        {activeTab === "addCandidates" && (
          // Pass down handleAddCandidate as prop for AddCandidateTab form submission
          <AddCandidateTab
            candidates={[
              ...jobSeekers.map((js) => ({ ...js, userType: "jobseeker"  })),
              ...hrUsers.map((hr) => ({ ...hr, userType: "HR"  })),
            ]}
            onAddCandidate={handleAddCandidate}
            onDeleteCandidate={handleDeleteCandidate}
          />

        )}

        {activeTab === "migration" && <MigrateTests />}

        {activeTab === "proPlusNotify" && <ProPlusNotifyTab />}

      </div>
    </div>
  );
};

export default AdminPanelDashboard;

